import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, X, CreditCard, Zap } from 'lucide-react';
import Button from '../common/Button';
import Card from '../common/Card';
import { purchaseCredits } from '../../lib/stripe';
import { useAuth } from '../../contexts/AuthContext';

interface PurchaseCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);

  const handlePurchase = async (amount: '100' | '500') => {
    if (!user) {
      alert('Please sign in to purchase credits');
      return;
    }

    try {
      setLoading(amount);
      await purchaseCredits(amount);
    } catch (error) {
      console.error('Failed to initiate credit purchase:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="credits-modal-title"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-md"
          >
            <Card className="relative bg-white">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="mx-auto w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <Coins className="h-6 w-6 text-primary-600" />
                  </div>
                  <h2 id="credits-modal-title" className="text-2xl font-bold text-gray-800 mb-2">
                    Purchase Credits
                  </h2>
                  <p className="text-gray-600">
                    Credits extend your session time beyond your daily plan limits
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <Card
                    className="p-4 border-2 border-gray-200 hover:border-primary-500 transition-colors cursor-pointer group"
                    onClick={() => handlePurchase('100')}
                    interactive
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <Zap className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800 group-hover:text-primary-600 transition-colors">
                            100 Credits
                          </h3>
                          <p className="text-sm text-gray-500">Perfect for occasional use</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-primary-600">$5</span>
                        <p className="text-xs text-gray-500">$0.05 per credit</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3"
                      isLoading={loading === '100'}
                      leftIcon={<CreditCard className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePurchase('100');
                      }}
                    >
                      {loading === '100' ? 'Processing...' : 'Purchase 100 Credits'}
                    </Button>
                  </Card>

                  <Card
                    className="p-4 border-2 border-primary-200 bg-primary-50 hover:border-primary-500 transition-colors cursor-pointer group relative"
                    onClick={() => handlePurchase('500')}
                    interactive
                  >
                    <div className="absolute -top-2 -right-2 bg-secondary-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      Best Value
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <Coins className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800 group-hover:text-primary-600 transition-colors">
                            500 Credits
                          </h3>
                          <p className="text-sm text-gray-500">Best value for regular users</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-primary-600">$20</span>
                        <p className="text-xs text-gray-500">$0.04 per credit</p>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full mt-3 bg-gradient-to-r from-primary-600 to-primary-500"
                      isLoading={loading === '500'}
                      leftIcon={<CreditCard className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePurchase('500');
                      }}
                    >
                      {loading === '500' ? 'Processing...' : 'Purchase 500 Credits'}
                    </Button>
                  </Card>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                    <Coins className="h-4 w-4 mr-2 text-primary-500" />
                    How Credits Work
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 1 credit = 1 minute of session time beyond your plan limit</li>
                    <li>• Credits never expire</li>
                    <li>• Used automatically when you exceed daily limits</li>
                    <li>• Works with any subscription plan</li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PurchaseCreditsModal;